import { forwardRef, useId, type ButtonHTMLAttributes, type ReactNode } from 'react';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks further clicks. */
  loading?: boolean;
  icon?: ReactNode;
  iconAfter?: ReactNode;
  fullWidth?: boolean;
  /**
   * Why a disabled button cannot be pressed yet, shown beside it. A disabled
   * button takes no hover and no click, so a `title` is never reliably seen:
   * to the person it is a button that "does nothing". Drawn before the button,
   * so it reads as the step that comes first, and only while the button is
   * disabled and not loading.
   */
  reason?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    iconAfter,
    fullWidth,
    disabled,
    children,
    className,
    type = 'button',
    reason,
    'aria-describedby': describedBy,
    ...rest
  },
  ref,
) {
  const reasonId = useId();
  const showReason = Boolean(reason && disabled && !loading);
  const button = (
    <button
      ref={ref}
      type={type}
      aria-describedby={
        [describedBy, showReason ? reasonId : null].filter(Boolean).join(' ') || undefined
      }
      // Kept enabled-looking but inert while loading: disabling outright moves
      // focus off the element, which loses a keyboard user's place mid-form.
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={[
        'btn',
        `btn--${variant}`,
        `btn--${size}`,
        fullWidth ? 'btn--full' : '',
        loading ? 'btn--loading' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading ? <span className="btn__spinner" aria-hidden /> : icon}
      {children ? <span className="btn__label">{children}</span> : null}
      {iconAfter}
    </button>
  );
  if (!showReason) return button;
  return (
    <>
      <span id={reasonId} className="btn-reason">
        {reason}
      </span>
      {button}
    </>
  );
});
